import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import MicroInteraction from "../micro-interaction/MicroInteraction";

interface PressToConfirmProps {
  /** ボタン下に表示するラベル文言（例: 「長押しで承認」） */
  label: string;
  /** 確定後の micro-interaction で表示する文言。既定は「ありがとう！」 */
  confirmedLabel?: string;
  /** 長押し完了時に一度だけ発火 */
  onConfirm: () => void;
  /**
   * 「OK 状態」の変化を双方向で親に通知する。
   * 1 周完了で true、外側クリックでリセットされたとき false が渡る。
   */
  onConfirmedChange?: (confirmed: boolean) => void;
  /** 長押し中（押し始め〜完了or解除）の状態変化を親に通知 */
  onActiveChange?: (active: boolean) => void;
  /**
   * 操作ロック（送信中など）。操作は受け付けないが、確定済み（OK）の
   * 見た目と状態は保持する（送信中に OFF へ戻って見えないようにする）。
   */
  disabled?: boolean;
  /**
   * OFF 状態の中央アイコン（material-symbols 名）。
   * 確定時は自動で `check` に切り替わる。既定は `chevron_right`。
   */
  iconName?: string;
  /** アクセントカラー。既定はテーマブルー。 */
  accentColor?: string;
  /** ボタン直径(px)。既定は 48。 */
  size?: number;
  /** 長押し時間(ms)。既定は 1500。 */
  holdDuration?: number;
}

// ===== レイアウト定数 =====

const STROKE_WIDTH = 4;
const RING_GAP = 4; // ボタン edge とリング内側の余白

// ===== スタイル =====

const Wrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  user-select: none;
`;

const ButtonArea = styled.div<{ $ringSize: number }>`
  position: relative;
  width: ${({ $ringSize }) => $ringSize}px;
  height: ${({ $ringSize }) => $ringSize}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Ring = styled.svg`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: visible;
`;

const Button = styled.div<{
  $size: number;
  $accent: string;
  $disabled: boolean;
  $isConfirmed: boolean;
  $isPressing: boolean;
  $holdDuration: number;
}>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
  /* 確定状態はロック（送信中）でも見た目を維持する（$disabled より優先） */
  cursor: ${({ $disabled, $isConfirmed }) => {
    if ($isConfirmed) return "default";
    if ($disabled) return "not-allowed";
    return "pointer";
  }};
  background: ${({ $disabled, $isConfirmed, $isPressing, $accent }) => {
    if ($isConfirmed) return $accent;
    if ($disabled) return "rgba(167, 168, 169, 0.6)";
    if ($isPressing) return "rgba(255, 255, 255, 0.92)";
    return "rgba(20, 24, 32, 0.30)";
  }};
  backdrop-filter: ${({ $isConfirmed, $disabled }) =>
    $isConfirmed || $disabled ? "none" : "blur(16px) saturate(160%)"};
  -webkit-backdrop-filter: ${({ $isConfirmed, $disabled }) =>
    $isConfirmed || $disabled ? "none" : "blur(16px) saturate(160%)"};
  border: 1px solid
    ${({ $disabled, $isConfirmed, $accent }) => {
      if ($isConfirmed) return $accent;
      if ($disabled) return "rgba(255, 255, 255, 0.14)";
      return "rgba(255, 255, 255, 0.24)";
    }};
  box-shadow: ${({ $disabled, $isConfirmed }) => {
    if ($isConfirmed) {
      return `
        0 0 0 1px rgba(255, 255, 255, 0.9),
        0 0 16px rgba(255, 255, 255, 0.7),
        0 2px 10px 4px rgba(0, 154, 206, 0.5),
        0 0 28px 8px rgba(0, 154, 206, 0.4)
      `;
    }
    if ($disabled) {
      return "0 2px 6px rgba(0, 0, 0, 0.15)";
    }
    return `
      inset 0 1px 0 rgba(255, 255, 255, 0.22),
      inset 0 -1px 0 rgba(0, 0, 0, 0.16)
    `;
  }};
  transform: ${({ $isPressing }) =>
    $isPressing ? "scale(0.97)" : "scale(1)"};
  /* 押下中は holdDuration ms で背景＆アイコン色をリニアに白／青へ。
     リリース or 確定時は素早く 0.25s で次状態へ。 */
  transition: ${({ $isPressing, $isConfirmed, $holdDuration }) => {
    if ($isPressing && !$isConfirmed) {
      return `
        background ${$holdDuration}ms linear,
        border-color 0.25s ease-out,
        box-shadow 0.25s ease-out,
        transform 0.18s ease-out
      `;
    }
    return `
      background 0.25s ease-out,
      border-color 0.25s ease-out,
      box-shadow 0.25s ease-out,
      transform 0.18s ease-out
    `;
  }};

  .material-symbols-rounded {
    /* OFF/Disabled/Confirmed は白。長押し中だけ背景が白に近づくのに合わせて青に推移。 */
    color: ${({ $isPressing, $isConfirmed, $disabled }) =>
      $isPressing && !$isConfirmed && !$disabled
        ? "var(--soremo-blue)"
        : "white"};
    font-size: ${({ $size }) => Math.round($size * 0.46)}px;
    filter: ${({ $isPressing, $isConfirmed }) =>
      $isPressing || $isConfirmed
        ? "none"
        : "drop-shadow(0 0 6px rgba(255, 255, 255, 0.35))"};
    transition: ${({ $isPressing, $isConfirmed, $holdDuration }) =>
      $isPressing && !$isConfirmed
        ? `color ${$holdDuration}ms linear`
        : "color 0.25s ease-out"};
  }
`;

const Label = styled.div`
  color: var(--soremo-light-gray);
  text-align: center;
  letter-spacing: 0.04em;
`;

// ===== 本体 =====

const PressToConfirm: React.FC<PressToConfirmProps> = ({
  label,
  confirmedLabel,
  onConfirm,
  onConfirmedChange,
  onActiveChange,
  disabled = false,
  iconName = "chevron_right",
  accentColor = "var(--soremo-blue)",
  size = 48,
  holdDuration = 1500,
}) => {
  const [isPressing, setIsPressing] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);

  // リング寸法
  const ringSize = size + RING_GAP * 2 + STROKE_WIDTH * 2;
  const ringRadius = ringSize / 2 - STROKE_WIDTH / 2;
  const circumference = 2 * Math.PI * ringRadius;

  // 親通知
  useEffect(() => {
    onConfirmedChange?.(isConfirmed);
  }, [isConfirmed, onConfirmedChange]);

  useEffect(() => {
    onActiveChange?.(isPressing);
  }, [isPressing, onActiveChange]);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // unmount で安全にタイマー解除
  useEffect(() => () => clearTimer(), [clearTimer]);

  // ロック時は押下中の進行だけ解除する。確定済み（OK）は送信中も
  // 見た目・状態を保持し、OFF へ戻る混乱を避ける（送信失敗後も確定の
  // ままなので、再長押しなしでリトライできる）。
  useEffect(() => {
    if (disabled) {
      clearTimer();
      setIsPressing(false);
    }
  }, [disabled, clearTimer]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || isConfirmed) return;
      e.preventDefault();
      setIsPressing(true);
      timerRef.current = window.setTimeout(() => {
        setIsConfirmed(true);
        setIsPressing(false);
        timerRef.current = null;
        onConfirm();
      }, holdDuration);
    },
    [disabled, isConfirmed, holdDuration, onConfirm],
  );

  const cancelPress = useCallback(() => {
    clearTimer();
    setIsPressing(false);
  }, [clearTimer]);

  // OK 状態のとき「ボタン外」クリックでリセット（送信中ロックの間は
  // 確定状態を保持するため反応しない）
  useEffect(() => {
    if (!isConfirmed || disabled) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // ボタン本体（コンテナ）の内側 → リセットしない
      if (containerRef.current?.contains(target)) return;
      // 他の interactive 要素のクリック（送信ボタン等）は尊重してリセットしない
      if (
        target.closest(
          "button, a, input, textarea, select, [role='button']",
        )
      ) {
        return;
      }
      setIsConfirmed(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [isConfirmed, disabled]);

  const iconToShow = isConfirmed ? "check" : iconName;
  const thanksText = confirmedLabel ?? "ありがとう！";

  // stroke-dashoffset: 押下中（または確定後）は 0（全周描画）、それ以外は circumference（不可視）。
  // 確定済みなら送信中ロックでも全周のまま維持する。
  const ringOffset = isPressing || isConfirmed ? 0 : circumference;

  // 押下中だけ holdDuration の linear で描画、それ以外はクイックに戻す/維持
  const ringTransition =
    isPressing && !isConfirmed
      ? `stroke-dashoffset ${holdDuration}ms linear`
      : "stroke-dashoffset 0.22s ease-out";

  return (
    <Wrapper>
      <MicroInteraction
        show={isConfirmed}
        text={thanksText}
        placement="top"
        offset={12}
        animationType="bounce"
        className="bodytext-11"
        trigger={
          <ButtonArea ref={containerRef} $ringSize={ringSize}>
            <Ring
              width={ringSize}
              height={ringSize}
              viewBox={`0 0 ${ringSize} ${ringSize}`}
              aria-hidden="true"
            >
              <circle
                cx={ringSize / 2}
                cy={ringSize / 2}
                r={ringRadius}
                fill="none"
                stroke={accentColor}
                strokeWidth={STROKE_WIDTH}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={ringOffset}
                transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
                style={{
                  transition: ringTransition,
                  /* 確定済みはロック中も通常の見た目を維持する */
                  opacity: disabled && !isConfirmed ? 0.35 : 1,
                }}
              />
            </Ring>
            <Button
              role="button"
              aria-label={isConfirmed ? thanksText : label}
              aria-pressed={isConfirmed}
              aria-disabled={disabled}
              $size={size}
              $accent={accentColor}
              $disabled={disabled}
              $isConfirmed={isConfirmed}
              $isPressing={isPressing}
              $holdDuration={holdDuration}
              onPointerDown={handlePointerDown}
              onPointerUp={cancelPress}
              onPointerCancel={cancelPress}
              onPointerLeave={cancelPress}
            >
              <span className="material-symbols-rounded">{iconToShow}</span>
            </Button>
          </ButtonArea>
        }
      />
      {/* 確定後は visibility だけ落として高さを保持（layout shift 抑止） */}
      <Label
        className="bodytext-11"
        style={{ visibility: isConfirmed ? "hidden" : "visible" }}
      >
        {label}
      </Label>
    </Wrapper>
  );
};

export default PressToConfirm;
